// src/pages/ELearning/PublicTestRunner.tsx — DESIGN v2
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Clock, Loader2, AlertCircle, Award, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, Check, CheckCircle2, Flag, Trophy, Sparkles
} from 'lucide-react';

interface Question {
  _id: string;
  questionText: string;
  questionType: 'single' | 'multiple' | 'text';
  options: { text: string }[];
  points: number;
}

interface TestData {
  _id: string;
  title: string;
  description: string;
  timeLimit: number;
}

const PublicTestRunner: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [test, setTest] = useState<TestData | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    const fetchTest = async () => {
      try {
        const res = await fetch(`/api/tests/public-elearning/${token}`);
        if (!res.ok) throw new Error('Тест не найден или уже пройден');
        const data = await res.json();
        setTest(data.test);
        setQuestions(data.questions);
      } catch (err: any) {
        setError(err.message || 'Ошибка загрузки теста');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchTest();
  }, [token]);

  const handleAnswer = (questionId: string, value: any) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const isAnswered = (qId: string): boolean => {
    const ans = answers[qId];
    if (ans === undefined || ans === null) return false;
    if (Array.isArray(ans)) return ans.length > 0;
    if (typeof ans === 'string') return ans.trim() !== '';
    return true;
  };

  const answeredCount = questions.filter(q => isAnswered(q._id)).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;
  const currentQ = questions[currentIdx];

  const goPrev = () => {
    if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
  };
  const goNext = () => {
    if (currentIdx < questions.length - 1) setCurrentIdx(currentIdx + 1);
  };

  const handleSubmit = async () => {
    const unanswered = questions.filter(q => !isAnswered(q._id));
    if (unanswered.length > 0) {
      alert(`Осталось ${unanswered.length} вопросов без ответа`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/tests/public-elearning/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, timeSpent: 0 }),
      });
      const data = await res.json();
      setResult(data);
      setSubmitted(true);
    } catch {
      setError('Ошибка отправки ответов');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={styles.loadingWrap}>
          <div style={styles.loadingIcon}>
            <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} color="#667eea" />
          </div>
          <p style={styles.loadingText}>Загрузка теста...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={styles.loadingWrap}>
          <div style={{...styles.loadingIcon, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
            <AlertCircle size={40} color="#fff" />
          </div>
          <h2 style={styles.errorTitle}>{error}</h2>
        </div>
      </>
    );
  }

  /* ═══ РЕЗУЛЬТАТ ═══ */
  if (submitted && result) {
    const passed = result.passed;
    const score = result.score || 0;
    const RADIUS = 80;
    const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
    const dashOffset = CIRCUMFERENCE * (1 - score / 100);
    const gradStart = passed ? '#11998e' : '#ef4444';
    const gradEnd = passed ? '#38ef7d' : '#dc2626';

    return (
      <>
        <style>{`
          @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes pop { 0% { transform: scale(0.7); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
          @keyframes confetti-fall {
            0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
            100% { transform: translateY(500px) rotate(720deg); opacity: 0; }
          }
          @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
          .ptr-result { animation: fadeUp 0.6s ease-out; }
          .ptr-trophy { animation: float 3s ease-in-out infinite; }
          .ptr-confetti {
            position: absolute;
            width: 10px; height: 10px; border-radius: 2px;
            animation: confetti-fall 3s ease-in forwards;
          }
        `}</style>
        <div style={styles.bg} />
        <div style={styles.resultContainer}>
          <div className="ptr-result" style={styles.resultCard}>
            {passed && (
              <div style={styles.confettiWrap}>
                {[...Array(30)].map((_, i) => (
                  <div
                    key={i}
                    className="ptr-confetti"
                    style={{
                      left: `${Math.random() * 100}%`,
                      background: ['#667eea', '#764ba2', '#f6d365', '#fda085', '#11998e', '#38ef7d', '#f5576c'][i % 7],
                      animationDelay: `${Math.random() * 1.5}s`,
                      animationDuration: `${2.5 + Math.random() * 1.5}s`,
                    }}
                  />
                ))}
              </div>
            )}

            <div className="ptr-trophy" style={{
              ...styles.resultIcon,
              background: passed
                ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: passed
                ? '0 16px 40px rgba(17, 153, 142, 0.4)'
                : '0 16px 40px rgba(239, 68, 68, 0.4)',
            }}>
              {passed ? <Trophy size={44} color="#fff" /> : <XCircle size={44} color="#fff" />}
            </div>

            <h1 style={{
              ...styles.resultTitle,
              background: passed
                ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              {passed ? '🎉 Поздравляем!' : '💪 Попробуйте ещё раз'}
            </h1>

            <p style={styles.resultSubtitle}>{result.message || (passed ? 'Вы успешно прошли тест' : 'Тест не пройден')}</p>

            {/* Круговой gauge */}
            <div style={styles.gaugeWrap}>
              <svg width="200" height="200">
                <defs>
                  <linearGradient id="ptrGradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={gradStart} />
                    <stop offset="100%" stopColor={gradEnd} />
                  </linearGradient>
                </defs>
                <circle cx="100" cy="100" r={RADIUS} stroke="rgba(0,0,0,0.06)" strokeWidth="12" fill="none" />
                <circle
                  cx="100" cy="100" r={RADIUS}
                  stroke="url(#ptrGradient)"
                  strokeWidth="12" fill="none" strokeLinecap="round"
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={dashOffset}
                  transform="rotate(-90 100 100)"
                  style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
                />
              </svg>
              <div style={styles.gaugeInner}>
                <div style={styles.gaugeScore}>
                  {score}<span style={styles.gaugePercent}>%</span>
                </div>
                <div style={styles.gaugeLabel}>результат</div>
              </div>
            </div>

            <div style={styles.brand}>
              <Sparkles size={14} color="#667eea" />
              <span style={{ fontSize: 12, color: '#8b8fa8', fontWeight: 600 }}>
                Powered by HR LAMA
              </span>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ═══ ПРОХОЖДЕНИЕ ═══ */
  if (!test || !currentQ) return null;

  const q = currentQ;
  const isFirst = currentIdx === 0;
  const isLast = currentIdx === questions.length - 1;

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes pop { 0% { transform: scale(0.8); } 60% { transform: scale(1.1); } 100% { transform: scale(1); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .ptr-hero { animation: fadeUp 0.5s ease-out; }
        .ptr-question { animation: slideIn 0.4s ease-out; }
        .ptr-opt { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); cursor: pointer; }
        .ptr-opt:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(102, 126, 234, 0.15) !important; border-color: rgba(102, 126, 234, 0.4) !important; }
        .ptr-opt-active { border-color: transparent !important; background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%) !important; box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25) !important; }
        .ptr-opt-active .ptr-opt-check { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; border-color: transparent !important; }
        .ptr-opt-active .ptr-opt-check-icon { opacity: 1 !important; }
        .ptr-opt-check { transition: all 0.25s ease; }
        .ptr-opt-check-icon { opacity: 0; transition: opacity 0.2s ease; animation: pop 0.3s ease; }
        .ptr-nav-dot { transition: all 0.25s ease; cursor: pointer; }
        .ptr-nav-dot:hover { transform: scale(1.15); }
        .ptr-btn-primary { transition: all 0.3s ease; }
        .ptr-btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4) !important; }
        .ptr-btn-ghost { transition: all 0.25s ease; }
        .ptr-btn-ghost:hover:not(:disabled) { background: rgba(102, 126, 234, 0.08) !important; border-color: rgba(102, 126, 234, 0.4) !important; }
        .ptr-progress-fill { transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
      `}</style>

      <div style={styles.bg} />
      <div style={styles.container}>
        {/* ═══ Sticky top bar ═══ */}
        <div className="ptr-hero" style={styles.topBar}>
          <div style={styles.topBarLeft}>
            <div style={styles.brandChip}>
              <Sparkles size={14} color="#fff" />
              <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: 0.5 }}>
                HR LAMA
              </span>
            </div>
            <h1 style={styles.testTitle}>{test.title}</h1>
            <div style={styles.topBarMeta}>
              <span style={styles.metaChip}>
                <Clock size={13} /> {test.timeLimit} мин
              </span>
              <span style={styles.metaChipBlue}>
                <Flag size={13} /> {answeredCount} / {questions.length}
              </span>
            </div>
          </div>

          <div style={styles.counterBlock}>
            <div style={styles.counterNumber}>
              {currentIdx + 1}<span style={styles.counterSlash}>/</span>{questions.length}
            </div>
            <div style={styles.counterLabel}>вопрос</div>
          </div>
        </div>

        {/* ═══ Progress ═══ */}
        <div style={styles.progressTrack}>
          <div className="ptr-progress-fill" style={{ ...styles.progressFill, width: `${progress}%` }} />
        </div>

        {/* ═══ Question ═══ */}
        <div key={q._id} className="ptr-question" style={styles.questionCard}>
          <div style={styles.qHeader}>
            <div style={styles.qBadge}>Вопрос {currentIdx + 1}</div>
            <div style={styles.qPoints}>
              {q.points} {q.points === 1 ? 'балл' : (q.points < 5 ? 'балла' : 'баллов')}
            </div>
          </div>

          <h2 style={styles.questionText}>{q.questionText}</h2>

          {q.questionType === 'multiple' && (
            <p style={styles.hintText}>💡 Можно выбрать несколько вариантов</p>
          )}

          {q.questionType === 'text' ? (
            <input
              type="text"
              placeholder="Введите ваш ответ..."
              value={answers[q._id] || ''}
              onChange={e => handleAnswer(q._id, e.target.value)}
              style={styles.textInput}
              autoFocus
            />
          ) : (
            <div style={styles.options}>
              {q.options.map((opt, i) => {
                const isSingle = q.questionType === 'single';
                const checked = isSingle
                  ? answers[q._id] === opt.text
                  : (answers[q._id] || []).includes(opt.text);

                return (
                  <div
                    key={i}
                    className={`ptr-opt ${checked ? 'ptr-opt-active' : ''}`}
                    onClick={() => {
                      if (isSingle) {
                        handleAnswer(q._id, opt.text);
                      } else {
                        const current = answers[q._id] || [];
                        if (checked) {
                          handleAnswer(q._id, current.filter((v: string) => v !== opt.text));
                        } else {
                          handleAnswer(q._id, [...current, opt.text]);
                        }
                      }
                    }}
                    style={styles.option}
                  >
                    <div className="ptr-opt-check" style={styles.optCheck}>
                      <Check size={14} color="#fff" className="ptr-opt-check-icon" strokeWidth={3} />
                    </div>
                    <span style={styles.optText}>{opt.text}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ═══ Navigator ═══ */}
        {questions.length > 1 && questions.length <= 30 && (
          <div style={styles.navigator}>
            {questions.map((qq, i) => {
              const done = isAnswered(qq._id);
              const isCurrent = i === currentIdx;
              return (
                <div
                  key={qq._id}
                  className="ptr-nav-dot"
                  onClick={() => setCurrentIdx(i)}
                  style={{
                    ...styles.navDot,
                    background: isCurrent
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : done
                      ? 'rgba(16, 185, 129, 0.6)'
                      : 'rgba(0,0,0,0.08)',
                    width: isCurrent ? 28 : 10,
                    boxShadow: isCurrent ? '0 4px 12px rgba(102, 126, 234, 0.4)' : 'none',
                  }}
                  title={`Вопрос ${i + 1}${done ? ' (отвечен)' : ''}`}
                />
              );
            })}
          </div>
        )}

        {/* ═══ Navigation ═══ */}
        <div style={styles.navButtons}>
          <button
            onClick={goPrev}
            disabled={isFirst}
            className="ptr-btn-ghost"
            style={{
              ...styles.ghostButton,
              opacity: isFirst ? 0.4 : 1,
              cursor: isFirst ? 'not-allowed' : 'pointer',
            }}
          >
            <ChevronLeft size={18} /> Назад
          </button>

          {!isLast ? (
            <button onClick={goNext} className="ptr-btn-primary" style={styles.primaryButton}>
              Далее <ChevronRight size={18} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || answeredCount < questions.length}
              className="ptr-btn-primary"
              style={{
                ...styles.submitButton,
                opacity: submitting || answeredCount < questions.length ? 0.6 : 1,
                cursor: submitting || answeredCount < questions.length ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? (
                <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Отправка...</>
              ) : (
                <><CheckCircle2 size={18} /> Завершить тест</>
              )}
            </button>
          )}
        </div>

        {!isLast && answeredCount === questions.length && (
          <div style={styles.quickSubmit}>
            <button onClick={handleSubmit} disabled={submitting} className="ptr-btn-primary" style={styles.submitButton}>
              {submitting ? (
                <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Отправка...</>
              ) : (
                <><CheckCircle2 size={18} /> Все готово — Завершить тест</>
              )}
            </button>
          </div>
        )}

        <div style={styles.brand}>
          <Sparkles size={12} color="#8b8fa8" />
          <span style={{ fontSize: 11, color: '#8b8fa8', fontWeight: 600 }}>
            Powered by HR LAMA
          </span>
        </div>
      </div>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  bg: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'linear-gradient(145deg, #f8fafd 0%, #e8f0fe 50%, #f5f0fb 100%)',
    zIndex: -1,
  },
  container: { maxWidth: 900, margin: '0 auto', padding: '32px 20px 80px', position: 'relative' },

  loadingWrap: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', gap: 20, fontFamily: '"Inter", "Segoe UI", sans-serif',
    background: 'linear-gradient(145deg, #f8fafd 0%, #e8f0fe 100%)',
  },
  loadingIcon: {
    width: 80, height: 80, borderRadius: 24,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 16px 40px rgba(102, 126, 234, 0.35)',
  },
  loadingText: { color: '#5a5f7a', fontSize: 15, fontWeight: 600 },
  errorTitle: { color: '#1a1a2e', fontSize: 22, fontWeight: 700, textAlign: 'center', padding: '0 20px' },

  topBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: 16, marginBottom: 16, flexWrap: 'wrap',
  },
  topBarLeft: { flex: 1, minWidth: 220 },
  brandChip: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '4px 10px', borderRadius: 8, marginBottom: 10,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  },
  testTitle: {
    fontSize: 24, fontWeight: 800, margin: 0, marginBottom: 8,
    color: '#1a1a2e', letterSpacing: '-0.5px', lineHeight: 1.2,
  },
  topBarMeta: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  metaChip: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '5px 10px', background: 'rgba(245, 158, 11, 0.12)',
    color: '#d97706', borderRadius: 10, fontSize: 12, fontWeight: 700,
  },
  metaChipBlue: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '5px 10px', background: 'rgba(102, 126, 234, 0.12)',
    color: '#667eea', borderRadius: 10, fontSize: 12, fontWeight: 700,
  },
  counterBlock: { textAlign: 'right', flexShrink: 0 },
  counterNumber: {
    fontSize: 32, fontWeight: 800,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    lineHeight: 1, letterSpacing: '-1px',
  },
  counterSlash: { opacity: 0.3, margin: '0 2px' },
  counterLabel: {
    fontSize: 10, color: '#8b8fa8', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 1, marginTop: 4,
  },

  progressTrack: {
    height: 6, background: 'rgba(102, 126, 234, 0.1)', borderRadius: 3,
    overflow: 'hidden', marginBottom: 28,
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: 3, minWidth: 4,
  },

  questionCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.9)',
    borderRadius: 28, padding: 32,
    boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)',
    marginBottom: 24,
  },
  qHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 20, gap: 12,
  },
  qBadge: {
    display: 'inline-flex', alignItems: 'center',
    padding: '5px 12px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff', borderRadius: 10, fontSize: 12, fontWeight: 800,
    letterSpacing: 0.5, textTransform: 'uppercase',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  },
  qPoints: {
    display: 'inline-flex', alignItems: 'center',
    padding: '5px 12px',
    background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
    color: '#fff', borderRadius: 10, fontSize: 12, fontWeight: 800,
    boxShadow: '0 4px 12px rgba(246, 211, 101, 0.4)',
  },
  questionText: {
    fontSize: 20, fontWeight: 700, color: '#1a1a2e',
    margin: '0 0 8px', lineHeight: 1.4,
  },
  hintText: {
    fontSize: 13, color: '#8b8fa8', margin: '0 0 20px',
    fontStyle: 'italic',
  },
  options: { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 },
  option: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '16px 20px',
    background: 'rgba(255, 255, 255, 0.7)',
    border: '2px solid rgba(0,0,0,0.06)',
    borderRadius: 16,
    boxShadow: '0 2px 8px rgba(31, 38, 135, 0.04)',
  },
  optCheck: {
    width: 24, height: 24, borderRadius: 8,
    border: '2px solid rgba(0,0,0,0.15)',
    background: 'rgba(255,255,255,0.9)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  optText: { fontSize: 15, fontWeight: 500, color: '#1a1a2e', lineHeight: 1.4, flex: 1 },
  textInput: {
    width: '100%', padding: '16px 20px',
    borderRadius: 16,
    border: '2px solid rgba(0,0,0,0.08)',
    background: 'rgba(255,255,255,0.9)',
    fontSize: 15, outline: 'none', marginTop: 20,
    boxSizing: 'border-box', fontFamily: 'inherit',
    transition: 'all 0.2s',
  },

  navigator: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    gap: 6, flexWrap: 'wrap',
    marginBottom: 24, padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRadius: 20,
    border: '1px solid rgba(255, 255, 255, 0.8)',
  },
  navDot: { height: 10, borderRadius: 5, background: 'rgba(0,0,0,0.08)' },

  navButtons: {
    display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center',
  },
  ghostButton: {
    padding: '14px 24px',
    background: 'rgba(255, 255, 255, 0.8)',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 16,
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontWeight: 700, fontSize: 15, color: '#5a5f7a',
    cursor: 'pointer',
  },
  primaryButton: {
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff', border: 'none', borderRadius: 16,
    display: 'inline-flex', alignItems: 'center', gap: 8,
    fontWeight: 700, fontSize: 15, cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)',
  },
  submitButton: {
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    color: '#fff', border: 'none', borderRadius: 16,
    display: 'inline-flex', alignItems: 'center', gap: 8,
    fontWeight: 700, fontSize: 15, cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(17, 153, 142, 0.35)',
  },
  quickSubmit: { marginTop: 20, textAlign: 'center', animation: 'fadeUp 0.4s ease-out' },

  brand: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 6, marginTop: 32, opacity: 0.7,
  },

  /* ═══ RESULT ═══ */
  resultContainer: { maxWidth: 560, margin: '60px auto', padding: '0 20px', position: 'relative' },
  resultCard: {
    position: 'relative',
    background: 'rgba(255, 255, 255, 0.75)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.9)',
    borderRadius: 32, padding: 40,
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(31, 38, 135, 0.12)',
    overflow: 'hidden',
  },
  confettiWrap: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    overflow: 'hidden', pointerEvents: 'none',
  },
  resultIcon: {
    width: 96, height: 96, borderRadius: 32,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px',
  },
  resultTitle: {
    fontSize: 30, fontWeight: 800, margin: '0 0 10px',
    letterSpacing: '-1px',
  },
  resultSubtitle: {
    fontSize: 15, color: '#5a5f7a', margin: '0 0 24px', lineHeight: 1.5,
  },
  gaugeWrap: { position: 'relative', width: 200, height: 200, margin: '20px auto' },
  gaugeInner: {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)', textAlign: 'center',
  },
  gaugeScore: {
    fontSize: 48, fontWeight: 800,
    background: 'linear-gradient(135deg, #1a1a2e 0%, #667eea 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    lineHeight: 1, letterSpacing: '-2px',
  },
  gaugePercent: { fontSize: 22, opacity: 0.5 },
  gaugeLabel: {
    fontSize: 10, color: '#8b8fa8', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 1.5, marginTop: 6,
  },
};

export default PublicTestRunner;
